<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useParticipantAuth } from '@/composables/useParticipantAuth'
import type { RegistrationFormData, FieldAnswer, Registration } from '@/types/registration'
import type { WizardPage } from '@/types/wizard-forms'
import type { User } from '@/types/auth'
import type { LegalDocumentKey } from '@/types/legal'
import RegistrationProgressBar from '@/components/registration/RegistrationProgressBar.vue'
import AccountStepForm from '@/components/registration/AccountStepForm.vue'
import WizardPageStepForm from '@/components/registration/WizardPageStepForm.vue'
import PaymentStep from '@/components/registration/PaymentStep.vue'
import WaitingListConfirmStep from '@/components/registration/WaitingListConfirmStep.vue'

const props = defineProps<{
  registrationData: RegistrationFormData
  fieldAnswers: Map<number, FieldAnswer>
  currentStep: number
  stepLabels: string[]
  isSubmitting: boolean
  submitError: string | null
  registration: Registration | null
  orgSlug: string
  eventSlug: string
  raceSlug: string
  isWaitingListMode: boolean
  isZeroPayment?: boolean
  voucherCode?: string | null
  raceRules: string | null
  termsAndConditions: string | null
}>()

const emit = defineEmits<{
  'authenticated': [user: User]
  'logged-in': [user: User]
  'update:answer': [wizardFieldId: number, value: string | null, selectedOptions?: number[]]
  'next': []
  'back': []
  'submit': []
  'open-legal-drawer': [doc: LegalDocumentKey, event: MouseEvent | KeyboardEvent]
}>()

const { t } = useI18n()
const { isParticipantAuthenticated } = useParticipantAuth()

// Step index offsets:
// 0 = account step
// 1..N = custom wizard pages
// N+1 = payment step

const wizardPages = computed((): WizardPage[] =>
  props.registrationData.wizard_form?.pages ?? [],
)

const totalSteps = computed(() => 1 + wizardPages.value.length + 1)

const isAccountStep = computed(() => props.currentStep === 0)
const isPaymentStep = computed(
  () => !props.isWaitingListMode && props.currentStep === totalSteps.value - 1,
)
const isWaitingListConfirmStep = computed(
  () => props.isWaitingListMode && props.currentStep === totalSteps.value - 1,
)
const isCustomPageStep = computed(
  () => !isAccountStep.value && !isPaymentStep.value && !isWaitingListConfirmStep.value,
)
const currentPageIndex = computed(() => props.currentStep - 1)
const currentPage = computed((): WizardPage | null =>
  isCustomPageStep.value ? (wizardPages.value[currentPageIndex.value] ?? null) : null,
)

// Track whether the AccountStepForm is currently saving the profile update
const isAccountStepSaving = ref(false)

// Reference to the AccountStepForm to trigger its form submission
const accountStepFormRef = ref<InstanceType<typeof AccountStepForm> | null>(null)

// Per-field validation errors for the current custom page
const pageValidationErrors = ref<Record<number, string>>({})

function validateCurrentPage(): boolean {
  if (!currentPage.value) return true
  const errors: Record<number, string> = {}
  for (const field of currentPage.value.fields ?? []) {
    if (!field.is_required) continue
    const answer = props.fieldAnswers.get(field.id)

    if (field.type === 'checkbox') {
      // checkbox boolean: not required in typical usage, skip
      continue
    }

    if (field.type === 'checkbox_group' || field.type === 'radio_group') {
      const selected = answer?.selected_options ?? []
      if (selected.length === 0) {
        errors[field.id] = t('registration.fields.validation_required')
      }
    } else {
      const val = answer?.value
      if (!val || val.trim() === '') {
        errors[field.id] = t('registration.fields.validation_required')
      }
    }
  }

  pageValidationErrors.value = errors
  return Object.keys(errors).length === 0
}

function handleNext(): void {
  if (isAccountStep.value) {
    const accepted = accountStepFormRef.value?.validateAcceptance() ?? true
    if (!accepted) return

    if (isParticipantAuthenticated.value) {
      // Trigger the account step form submission which will save profile updates
      // The profile-updated event handler will advance to next step
      accountStepFormRef.value?.submitForm()
      return
    }
  }
  if (isCustomPageStep.value && !validateCurrentPage()) return
  emit('next')
}

function handleBack(): void {
  pageValidationErrors.value = {}
  emit('back')
}

function handleRegistered(user: User): void {
  // After a fresh registration, advance to next step (data was just entered)
  emit('authenticated', user)
  // If there are no custom pages, account step is right before payment:
  // submit the registration instead of just advancing
  if (wizardPages.value.length === 0) {
    emit('submit')
  } else {
    emit('next')
  }
}

function handleAuthenticated(user: User): void {
  // After login: emit authenticated but stay on step 0 (the AccountStepForm
  // switches to authenticated mode, showing the user's data for review)
  emit('authenticated', user)
}

function handleLoggedIn(user: User): void {
  // Distinct from a fresh registration: relayed only when the account step
  // authenticated via an existing session (login, or a discovered session),
  // so the page can re-check for an in-progress registration for this race.
  emit('logged-in', user)
}

function handleProfileUpdated(_user: User): void {
  isAccountStepSaving.value = false
  // If there are no custom pages, account step is right before payment:
  // submit the registration instead of just advancing
  if (wizardPages.value.length === 0) {
    emit('submit')
  } else {
    emit('next')
  }
}

function handleLoggedOut(): void {
  // User logged out from the authenticated account step.
  // Stay on step 0 which will now show register/login mode.
}

function handleSubmit(): void {
  if (isCustomPageStep.value && !validateCurrentPage()) return
  emit('submit')
}
</script>

<template>
  <div class="space-y-6">
    <!-- Progress bar -->
    <RegistrationProgressBar
      :step-labels="stepLabels"
      :current-step="currentStep"
    />

    <!-- Waiting list banner -->
    <div
      v-if="isWaitingListMode && !isWaitingListConfirmStep"
      class="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700"
    >
      {{ t('registration.waiting_list.banner') }}
    </div>

    <!-- Submission error banner -->
    <div
      v-if="submitError"
      class="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600"
    >
      {{ submitError.includes(' ') ? submitError : t(submitError) }}
    </div>

    <!-- Step content -->
    <div class="bg-white rounded-2xl border border-slate-200 p-6">
      <!-- Account step -->
      <AccountStepForm
        v-if="isAccountStep"
        ref="accountStepFormRef"
        :race-rules="raceRules"
        :terms-and-conditions="termsAndConditions"
        @registered="handleRegistered"
        @authenticated="handleAuthenticated"
        @logged-in="handleLoggedIn"
        @profile-updated="handleProfileUpdated"
        @logged-out="handleLoggedOut"
        @open-legal-drawer="(doc, ev) => emit('open-legal-drawer', doc, ev)"
      />

      <!-- Custom wizard page steps -->
      <WizardPageStepForm
        v-else-if="isCustomPageStep && currentPage"
        :page="currentPage"
        :field-answers="fieldAnswers"
        :validation-errors="pageValidationErrors"
        @update:answer="(id, val, opts) => emit('update:answer', id, val, opts)"
      />

      <!-- Zero-payment confirmation (voucher covers full cost) -->
      <div
        v-else-if="isPaymentStep && registration && props.isZeroPayment"
        class="space-y-6"
      >
        <div>
          <h2 class="text-xl font-bold text-slate-900 mb-1">{{ t('registration.voucher.no_payment_required') }}</h2>
        </div>

        <div class="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 px-5 py-4">
          <svg class="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p class="text-sm text-emerald-700 font-medium">
            {{ props.voucherCode ? t('registration.voucher.no_payment_message') : t('registration.voucher.free_race_message') }}
          </p>
            <p v-if="props.voucherCode" class="text-xs text-emerald-600 mt-1">
              <span class="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-0.5 text-xs font-medium">
                {{ props.voucherCode }} {{ t('registration.voucher.applied') }}
              </span>
            </p>
          </div>
        </div>

        <RouterLink
          :to="`/${orgSlug}/${eventSlug}/${raceSlug}/confirmation/${registration.confirmation_token}`"
          class="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg px-4 py-3 text-sm transition-colors"
        >
          {{ t('registration.confirmation.heading') }}
        </RouterLink>
      </div>

      <!-- Payment step -->
      <PaymentStep
        v-else-if="isPaymentStep && registration"
        :registration="registration"
        :org-slug="orgSlug"
        :event-slug="eventSlug"
        :race-slug="raceSlug"
        :seen-price-cents="registrationData.race.pricing.current_price_cents"
      />

      <!-- Waiting list confirm step -->
      <WaitingListConfirmStep
        v-else-if="isWaitingListConfirmStep && registration"
        :registration="registration"
        :registration-data="registrationData"
      />

      <!-- Waiting for registration to be saved (submitting state on final step) -->
      <div
        v-else-if="(isPaymentStep || isWaitingListConfirmStep) && isSubmitting"
        class="flex items-center justify-center py-12"
      >
        <svg class="animate-spin h-8 w-8 text-emerald-500" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span class="ml-3 text-slate-600 text-sm">{{ t('registration.submitting') }}</span>
      </div>
    </div>

    <!-- Navigation buttons for authenticated account step -->
    <div
      v-if="isAccountStep && isParticipantAuthenticated"
      class="flex items-center justify-end gap-4"
    >
      <!-- Account step with no custom pages: submit registration directly -->
      <button
        v-if="totalSteps === 2"
        type="button"
        :disabled="isSubmitting || isAccountStepSaving"
        class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
        @click="isSubmitting ? undefined : handleNext()"
      >
        <svg v-if="isSubmitting || isAccountStepSaving" class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        {{ (isSubmitting || isAccountStepSaving) ? t('registration.submitting') : (isWaitingListMode ? t('registration.waiting_list.join_button') : t('registration.proceed_to_payment')) }}
      </button>

      <button
        v-else
        type="button"
        :disabled="isAccountStepSaving"
        class="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
        @click="handleNext"
      >
        <svg v-if="isAccountStepSaving" class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        {{ isAccountStepSaving ? t('common.loading') : t('registration.next') }} 
        <svg v-if="!isAccountStepSaving" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>

    <!-- Navigation buttons for custom page steps -->
    <div
      v-if="!isAccountStep && !isPaymentStep && !isWaitingListConfirmStep"
      class="flex items-center justify-between gap-4"
    >
      <button
        type="button"
        class="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
        @click="handleBack"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        {{ t('registration.back') }}
      </button>

      <!-- Last custom page before payment: submit registration -->
      <button
        v-if="currentStep === totalSteps - 2"
        type="button"
        :disabled="isSubmitting"
        class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
        @click="isSubmitting ? undefined : handleSubmit()"
      >
        <svg v-if="isSubmitting" class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        {{ isSubmitting ? t('registration.submitting') : (isWaitingListMode ? t('registration.waiting_list.join_button') : t('registration.proceed_to_payment')) }}
      </button>

      <button
        v-else
        type="button"
        class="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
        @click="handleNext"
      >
        {{ t('registration.next') }}
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>

    <!-- Back button on payment/waiting-list step (before registration is submitted) -->
    <div v-if="(isPaymentStep || isWaitingListConfirmStep) && !registration && !isSubmitting" class="flex items-center">
      <button
        type="button"
        class="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
        @click="handleBack"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        {{ t('registration.back') }}
      </button>
    </div>
  </div>
</template>
